import { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Plus, Users, Building2, Eye, Power, PowerOff, PencilLine, RefreshCw, Search,
  ScrollText, History,
} from "lucide-react";
import { toast } from "sonner";

/* ---------- Utilitários de documento (mesmo algoritmo do backend F7) ---------- */

const soDigitos = (s) => (s || "").replace(/\D+/g, "");

function validarCPF(digits) {
  if (digits.length !== 11 || /^(\d)\1{10}$/.test(digits)) return false;
  let s = 0;
  for (let i = 0; i < 9; i++) s += parseInt(digits[i], 10) * (10 - i);
  let dv1 = (s * 10) % 11;
  if (dv1 === 10) dv1 = 0;
  if (dv1 !== parseInt(digits[9], 10)) return false;
  s = 0;
  for (let i = 0; i < 10; i++) s += parseInt(digits[i], 10) * (11 - i);
  let dv2 = (s * 10) % 11;
  if (dv2 === 10) dv2 = 0;
  return dv2 === parseInt(digits[10], 10);
}

function validarCNPJ(digits) {
  if (digits.length !== 14 || /^(\d)\1{13}$/.test(digits)) return false;
  const pesos1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const pesos2 = [6, ...pesos1];
  let s1 = 0;
  for (let i = 0; i < 12; i++) s1 += parseInt(digits[i], 10) * pesos1[i];
  let dv1 = 11 - (s1 % 11);
  if (dv1 >= 10) dv1 = 0;
  if (dv1 !== parseInt(digits[12], 10)) return false;
  let s2 = 0;
  for (let i = 0; i < 13; i++) s2 += parseInt(digits[i], 10) * pesos2[i];
  let dv2 = 11 - (s2 % 11);
  if (dv2 >= 10) dv2 = 0;
  return dv2 === parseInt(digits[13], 10);
}

function formatarCPF(digits) {
  const d = digits.padEnd(11, "").slice(0, 11);
  return d.replace(/^(\d{3})(\d{3})(\d{3})(\d{2}).*/, "$1.$2.$3-$4");
}
function formatarCNPJ(digits) {
  const d = digits.padEnd(14, "").slice(0, 14);
  return d.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2}).*/, "$1.$2.$3/$4-$5");
}
function formatarDocumento(tipo, raw) {
  const d = soDigitos(raw);
  if (!d) return "—";
  if (tipo === "PF") {
    if (d.length === 11) return formatarCPF(d);
    return d;
  }
  if (tipo === "PJ") {
    if (d.length === 14) return formatarCNPJ(d);
    return d;
  }
  return d;
}
function mascaraDocumentoOnInput(tipo, valor) {
  const d = soDigitos(valor).slice(0, tipo === "PF" ? 11 : 14);
  if (!d) return "";
  if (tipo === "PF") {
    if (d.length <= 3) return d;
    if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
    if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
    return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
  }
  // PJ
  if (d.length <= 2) return d;
  if (d.length <= 5) return `${d.slice(0, 2)}.${d.slice(2)}`;
  if (d.length <= 8) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5)}`;
  if (d.length <= 12) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8)}`;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
}
function mascaraCEP(v) {
  const d = soDigitos(v).slice(0, 8);
  if (d.length <= 5) return d;
  return `${d.slice(0, 5)}-${d.slice(5)}`;
}
function mascaraTelefone(v) {
  const d = soDigitos(v).slice(0, 11);
  if (d.length <= 2) return d;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

/* ---------- Constantes ---------- */

const UFS = ["AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG",
             "PA","PB","PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO"];

const emptyForm = () => ({
  tipo_pessoa: "PJ",
  documento: "",
  razao_social_nome: "",
  nome_fantasia: "",
  email: "",
  telefone: "",
  celular: "",
  endereco: "",
  cidade: "",
  estado: "",
  cep: "",
  observacoes: "",
});

/* ---------- Componentes visuais reaproveitados ---------- */

function StatusBadge({ ativo }) {
  return (
    <span
      data-testid={ativo ? "forn-status-ativo" : "forn-status-inativo"}
      className={`inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-semibold ${
        ativo
          ? "bg-[#EAF0EA] text-[#083A6B] border border-[#083A6B]/20"
          : "bg-stone-100 text-stone-500 border border-stone-300"
      }`}
    >
      {ativo ? "Ativo" : "Inativo"}
    </span>
  );
}

function TipoTag({ tipo }) {
  const isPJ = tipo === "PJ";
  const Icon = isPJ ? Building2 : Users;
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-stone-700">
      <Icon size={14} strokeWidth={1.6} className="text-[#083A6B]" />
      {isPJ ? "PJ" : "PF"}
    </span>
  );
}

const acaoLabel = {
  criada: "Criado",
  atualizada: "Atualizado",
  ativada: "Ativado",
  desativada: "Desativado",
};

/* ---------- Página ---------- */

export default function Fornecedores() {
  const [items, setItems] = useState([]);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erroCarga, setErroCarga] = useState(null);

  const [busca, setBusca] = useState("");
  const [filtroTipo, setFiltroTipo] = useState("all");
  const [filtroStatus, setFiltroStatus] = useState("all"); // all|ativo|inativo
  const [filtroDoc, setFiltroDoc] = useState("");

  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [docErro, setDocErro] = useState("");

  const [detalheAlvo, setDetalheAlvo] = useState(null);
  const [detalheOpen, setDetalheOpen] = useState(false);
  const [detalheLoading, setDetalheLoading] = useState(false);

  const [confirmAlvo, setConfirmAlvo] = useState(null);
  const [toggling, setToggling] = useState(false);

  const [auditOpen, setAuditOpen] = useState(false);
  const [auditAlvo, setAuditAlvo] = useState(null);
  const [auditItems, setAuditItems] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);

  const podeEscrever = !!(me && (me.is_admin || me.is_finance));

  const load = useCallback(async () => {
    setLoading(true); setErroCarga(null);
    try {
      const params = {};
      if (filtroTipo !== "all") params.tipo_pessoa = filtroTipo;
      if (filtroStatus !== "all") params.ativo = filtroStatus === "ativo";
      const docNorm = soDigitos(filtroDoc);
      if (docNorm) params.documento = docNorm;
      const { data } = await api.get("/fornecedores", { params });
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      setItems([]);
      setErroCarga(
        err?.response?.status === 401
          ? "Sessão expirada. Faça login novamente."
          : "Não foi possível carregar os fornecedores.",
      );
    } finally {
      setLoading(false);
    }
  }, [filtroTipo, filtroStatus, filtroDoc]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setMe(data);
      } catch {
        setMe(null);
      }
    })();
  }, []);

  const itensFiltradosBusca = useMemo(() => {
    const q = busca.trim().toLowerCase();
    if (!q) return items;
    return items.filter((f) => {
      const alvo = [f.razao_social_nome, f.nome_fantasia, f.email, f.cidade, f.estado,
                    formatarDocumento(f.tipo_pessoa, f.documento_normalizado)]
        .filter(Boolean).join(" ").toLowerCase();
      return alvo.includes(q);
    });
  }, [items, busca]);

  /* ---------- Ações ---------- */

  const abrirNovo = () => {
    setEditingId(null);
    setForm(emptyForm());
    setDocErro("");
    setFormOpen(true);
  };

  const abrirEdicao = (f) => {
    setEditingId(f.id);
    setForm({
      tipo_pessoa: f.tipo_pessoa || "PJ",
      documento: f.documento || (f.documento_normalizado
        ? mascaraDocumentoOnInput(f.tipo_pessoa || "PJ", f.documento_normalizado)
        : ""),
      razao_social_nome: f.razao_social_nome || "",
      nome_fantasia: f.nome_fantasia || "",
      email: f.email || "",
      telefone: f.telefone || "",
      celular: f.celular || "",
      endereco: f.endereco || "",
      cidade: f.cidade || "",
      estado: f.estado || "",
      cep: f.cep || "",
      observacoes: f.observacoes || "",
    });
    setDocErro("");
    setFormOpen(true);
  };

  const onChangeDocumento = (raw) => {
    const mascarado = mascaraDocumentoOnInput(form.tipo_pessoa, raw);
    setForm((f) => ({ ...f, documento: mascarado }));
    const digits = soDigitos(mascarado);
    if (!digits) { setDocErro(""); return; }
    if (form.tipo_pessoa === "PF") {
      if (digits.length < 11) setDocErro("CPF incompleto");
      else if (!validarCPF(digits)) setDocErro("CPF inválido");
      else setDocErro("");
    } else {
      if (digits.length < 14) setDocErro("CNPJ incompleto");
      else if (!validarCNPJ(digits)) setDocErro("CNPJ inválido");
      else setDocErro("");
    }
  };

  const onChangeTipoPessoa = (v) => {
    if (editingId) return; // imutável em edição
    setForm((f) => ({ ...f, tipo_pessoa: v, documento: "" }));
    setDocErro("");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.razao_social_nome.trim()) {
      return toast.error(form.tipo_pessoa === "PJ" ? "Informe a razão social" : "Informe o nome");
    }
    const docDigits = soDigitos(form.documento);
    if (docDigits) {
      if (form.tipo_pessoa === "PF" && !validarCPF(docDigits)) {
        setDocErro("CPF inválido");
        return toast.error("CPF inválido");
      }
      if (form.tipo_pessoa === "PJ" && !validarCNPJ(docDigits)) {
        setDocErro("CNPJ inválido");
        return toast.error("CNPJ inválido");
      }
    }
    const payload = {
      documento: form.documento.trim(),
      razao_social_nome: form.razao_social_nome.trim(),
      nome_fantasia: form.nome_fantasia.trim(),
      email: form.email.trim(),
      telefone: form.telefone.trim(),
      celular: form.celular.trim(),
      endereco: form.endereco.trim(),
      cidade: form.cidade.trim(),
      estado: (form.estado || "").trim().toUpperCase(),
      cep: form.cep.trim(),
      observacoes: form.observacoes.trim(),
    };
    setSaving(true);
    try {
      if (editingId) {
        await api.patch(`/fornecedores/${editingId}`, payload);
        toast.success("Fornecedor atualizado");
      } else {
        await api.post("/fornecedores", { ...payload, tipo_pessoa: form.tipo_pessoa });
        toast.success("Fornecedor cadastrado");
      }
      setFormOpen(false);
      setEditingId(null);
      setForm(emptyForm());
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Erro ao salvar. Verifique os dados.";
      toast.error(String(msg));
    } finally {
      setSaving(false);
    }
  };

  const abrirDetalhe = async (f) => {
    setDetalheAlvo(f); setDetalheOpen(true); setDetalheLoading(true);
    try {
      const { data } = await api.get(`/fornecedores/${f.id}`);
      setDetalheAlvo(data);
    } catch {
      // mantém o alvo básico
    } finally {
      setDetalheLoading(false);
    }
  };

  const confirmarToggle = (f) => setConfirmAlvo(f);

  const executarToggle = async () => {
    if (!confirmAlvo) return;
    const acao = confirmAlvo.ativo ? "desativar" : "ativar";
    setToggling(true);
    try {
      await api.post(`/fornecedores/${confirmAlvo.id}/${acao}`);
      toast.success(acao === "desativar" ? "Fornecedor desativado" : "Fornecedor ativado");
      setConfirmAlvo(null);
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail || `Erro ao ${acao} o fornecedor`;
      toast.error(String(msg));
    } finally {
      setToggling(false);
    }
  };

  const abrirAuditoria = async (f) => {
    setAuditAlvo(f); setAuditOpen(true); setAuditLoading(true); setAuditItems([]);
    try {
      const { data } = await api.get("/fornecedores/auditoria", {
        params: { fornecedor_id: f.id, limit: 100 },
      });
      setAuditItems(Array.isArray(data) ? data : []);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Não foi possível carregar a auditoria.";
      toast.error(String(msg));
    } finally {
      setAuditLoading(false);
    }
  };

  /* ---------- Render ---------- */

  const nomeCampoLabel = form.tipo_pessoa === "PJ" ? "Razão Social" : "Nome completo";
  const docLabel = form.tipo_pessoa === "PJ" ? "CNPJ" : "CPF";
  const docPlaceholder = form.tipo_pessoa === "PJ" ? "00.000.000/0000-00" : "000.000.000-00";

  return (
    <div data-testid="page-fornecedores">
      <PageHeader
        eyebrow="Financeiro · Fornecedores"
        title="Fornecedores"
        description="Cadastro estruturado de fornecedores (PF/PJ) como dimensão reutilizável nos módulos financeiros."
        actions={
          podeEscrever ? (
            <Dialog open={formOpen} onOpenChange={(v) => {
              setFormOpen(v);
              if (!v) { setEditingId(null); setForm(emptyForm()); setDocErro(""); }
            }}>
              <DialogTrigger asChild>
                <Button data-testid="btn-novo-fornecedor" onClick={abrirNovo}>
                  <Plus size={16} /> Novo fornecedor
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>
                    {editingId ? "Editar fornecedor" : "Novo fornecedor"}
                  </DialogTitle>
                  <DialogDescription>
                    {editingId
                      ? "O tipo de pessoa não pode ser alterado após o cadastro."
                      : "Cadastro estruturado. O CPF/CNPJ é validado estruturalmente (sem consulta à Receita)."}
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="forn-tipo">Tipo de pessoa *</Label>
                    <Select value={form.tipo_pessoa} onValueChange={onChangeTipoPessoa}
                            disabled={!!editingId}>
                      <SelectTrigger id="forn-tipo" data-testid="select-forn-tipo">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="PJ">PJ — Pessoa Jurídica</SelectItem>
                        <SelectItem value="PF">PF — Pessoa Física</SelectItem>
                      </SelectContent>
                    </Select>
                    {editingId && (
                      <p className="text-[11px] text-stone-500 mt-1">
                        Tipo de pessoa não pode ser alterado após o cadastro.
                      </p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="forn-doc">{docLabel}</Label>
                    <Input id="forn-doc" data-testid="input-forn-documento"
                      value={form.documento}
                      onChange={(e) => onChangeDocumento(e.target.value)}
                      placeholder={docPlaceholder}
                      aria-invalid={!!docErro} />
                    {docErro ? (
                      <p className="text-[11px] text-[#D4A652] mt-1">{docErro}</p>
                    ) : (
                      <p className="text-[11px] text-stone-500 mt-1">
                        Opcional. Se informado, precisa ser válido.
                      </p>
                    )}
                  </div>

                  <div className="md:col-span-2">
                    <Label htmlFor="forn-razao">{nomeCampoLabel} *</Label>
                    <Input id="forn-razao" data-testid="input-forn-razao"
                      value={form.razao_social_nome}
                      onChange={(e) => setForm({ ...form, razao_social_nome: e.target.value })}
                      placeholder={form.tipo_pessoa === "PJ" ? "Ex.: Padaria da Cida Ltda" : "Ex.: João da Silva"}
                      required />
                  </div>

                  <div className="md:col-span-2">
                    <Label htmlFor="forn-fantasia">Nome fantasia</Label>
                    <Input id="forn-fantasia" data-testid="input-forn-fantasia"
                      value={form.nome_fantasia}
                      onChange={(e) => setForm({ ...form, nome_fantasia: e.target.value })}
                      placeholder="Como aparece publicamente (opcional)" />
                  </div>

                  <div>
                    <Label htmlFor="forn-email">E-mail</Label>
                    <Input id="forn-email" type="email" data-testid="input-forn-email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="contato@fornecedor.com" />
                  </div>
                  <div>
                    <Label htmlFor="forn-tel">Telefone</Label>
                    <Input id="forn-tel" data-testid="input-forn-telefone"
                      value={form.telefone}
                      onChange={(e) => setForm({ ...form, telefone: mascaraTelefone(e.target.value) })}
                      placeholder="(11) 3000-0000" />
                  </div>
                  <div>
                    <Label htmlFor="forn-cel">Celular</Label>
                    <Input id="forn-cel" data-testid="input-forn-celular"
                      value={form.celular}
                      onChange={(e) => setForm({ ...form, celular: mascaraTelefone(e.target.value) })}
                      placeholder="(11) 90000-0000" />
                  </div>
                  <div>
                    <Label htmlFor="forn-cep">CEP</Label>
                    <Input id="forn-cep" data-testid="input-forn-cep"
                      value={form.cep}
                      onChange={(e) => setForm({ ...form, cep: mascaraCEP(e.target.value) })}
                      placeholder="00000-000" />
                  </div>

                  <div className="md:col-span-2">
                    <Label htmlFor="forn-end">Endereço</Label>
                    <Input id="forn-end" data-testid="input-forn-endereco"
                      value={form.endereco}
                      onChange={(e) => setForm({ ...form, endereco: e.target.value })}
                      placeholder="Rua, número, bairro, complemento" />
                  </div>
                  <div>
                    <Label htmlFor="forn-cidade">Cidade</Label>
                    <Input id="forn-cidade" data-testid="input-forn-cidade"
                      value={form.cidade}
                      onChange={(e) => setForm({ ...form, cidade: e.target.value })} />
                  </div>
                  <div>
                    <Label htmlFor="forn-uf">Estado (UF)</Label>
                    <Select value={form.estado || "none"}
                            onValueChange={(v) => setForm({ ...form, estado: v === "none" ? "" : v })}>
                      <SelectTrigger id="forn-uf" data-testid="select-forn-uf">
                        <SelectValue placeholder="—" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">—</SelectItem>
                        {UFS.map((uf) => <SelectItem key={uf} value={uf}>{uf}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="md:col-span-2">
                    <Label htmlFor="forn-obs">Observações</Label>
                    <Input id="forn-obs" data-testid="input-forn-observacoes"
                      value={form.observacoes}
                      onChange={(e) => setForm({ ...form, observacoes: e.target.value })} />
                  </div>

                  <DialogFooter className="md:col-span-2">
                    <Button type="button" variant="outline"
                      onClick={() => { setFormOpen(false); setEditingId(null); setForm(emptyForm()); setDocErro(""); }}>
                      Cancelar
                    </Button>
                    <Button type="submit" data-testid="btn-salvar-forn" disabled={saving || !!docErro}>
                      {saving ? "Salvando..." : editingId ? "Salvar alterações" : "Cadastrar fornecedor"}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          ) : null
        }
      />

      {/* Filtros */}
      <div className="flex flex-wrap items-end gap-3 mb-6">
        <div className="flex-1 min-w-[220px]">
          <Label htmlFor="forn-busca" className="text-[11px] uppercase tracking-widest text-stone-500">Busca</Label>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <Input id="forn-busca" data-testid="input-forn-busca" value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Razão social, nome fantasia, e-mail, cidade..."
              className="pl-8" />
          </div>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-widest text-stone-500">Tipo</Label>
          <Select value={filtroTipo} onValueChange={setFiltroTipo}>
            <SelectTrigger data-testid="select-filtro-forn-tipo" className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os tipos</SelectItem>
              <SelectItem value="PJ">Pessoa Jurídica (PJ)</SelectItem>
              <SelectItem value="PF">Pessoa Física (PF)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-widest text-stone-500">Status</Label>
          <Select value={filtroStatus} onValueChange={setFiltroStatus}>
            <SelectTrigger data-testid="select-filtro-forn-status" className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="ativo">Ativo</SelectItem>
              <SelectItem value="inativo">Inativo</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="forn-doc-filtro" className="text-[11px] uppercase tracking-widest text-stone-500">
            Documento
          </Label>
          <Input id="forn-doc-filtro" data-testid="input-filtro-forn-documento"
            value={filtroDoc}
            onChange={(e) => setFiltroDoc(e.target.value)}
            placeholder="CPF ou CNPJ"
            className="w-[180px]" />
        </div>
        <Button variant="outline" size="sm" onClick={load} aria-label="Atualizar lista"
                data-testid="btn-refresh-forn">
          <RefreshCw size={14} />
        </Button>
      </div>

      {/* Conteúdo */}
      {loading ? (
        <div data-testid="forn-loading"
             className="bg-[#FFFFFF] border border-stone-200 rounded-lg p-10 text-center text-stone-500">
          Carregando fornecedores...
        </div>
      ) : erroCarga ? (
        <div data-testid="forn-erro"
             className="bg-[#FFFFFF] border border-[#D4A652]/30 rounded-lg p-10 text-center text-[#D4A652]">
          {erroCarga}
        </div>
      ) : itensFiltradosBusca.length === 0 ? (
        <div data-testid="forn-empty"
             className="bg-[#FFFFFF] border border-stone-200 rounded-lg p-12 text-center">
          <div className="w-14 h-14 rounded-full bg-[#EAF0EA] flex items-center justify-center mx-auto mb-4 text-[#083A6B]">
            <Building2 size={22} strokeWidth={1.5} />
          </div>
          <div className="font-serif text-2xl text-stone-900 mb-2">
            Nenhum fornecedor encontrado.
          </div>
          <p className="text-sm text-stone-600 mb-6 max-w-md mx-auto">
            {items.length === 0
              ? "Cadastre o primeiro fornecedor para começar a organizar suas compras e despesas."
              : "Nenhum fornecedor corresponde aos filtros aplicados."}
          </p>
          {podeEscrever && items.length === 0 && (
            <Button data-testid="btn-empty-cadastrar-forn" onClick={abrirNovo}>
              <Plus size={16} /> Cadastrar primeiro fornecedor
            </Button>
          )}
        </div>
      ) : (
        <>
          {/* Mobile: cards */}
          <div className="md:hidden space-y-3">
            {itensFiltradosBusca.map((f) => (
              <div key={f.id} data-testid={`forn-card-${f.id}`}
                   className="bg-[#FFFFFF] border border-stone-200 rounded-lg p-4">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="min-w-0">
                    <div className="font-serif text-lg text-stone-900 truncate">
                      {f.razao_social_nome}
                    </div>
                    <TipoTag tipo={f.tipo_pessoa} />
                  </div>
                  <StatusBadge ativo={f.ativo} />
                </div>
                <div className="text-xs text-stone-600 space-y-1 mb-3">
                  {f.nome_fantasia && <div><b>Fantasia:</b> {f.nome_fantasia}</div>}
                  {f.documento_normalizado && (
                    <div>
                      <b>{f.tipo_pessoa === "PJ" ? "CNPJ:" : "CPF:"}</b>{" "}
                      {formatarDocumento(f.tipo_pessoa, f.documento_normalizado)}
                    </div>
                  )}
                  {f.email && <div><b>E-mail:</b> {f.email}</div>}
                  {(f.telefone || f.celular) && (
                    <div><b>Contato:</b> {f.celular || f.telefone}</div>
                  )}
                  {(f.cidade || f.estado) && (
                    <div><b>Local:</b> {[f.cidade, f.estado].filter(Boolean).join(" / ")}</div>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={() => abrirDetalhe(f)}
                          data-testid={`btn-forn-detalhe-${f.id}`}>
                    <Eye size={14} /> Detalhes
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => abrirAuditoria(f)}
                          data-testid={`btn-forn-auditoria-${f.id}`}>
                    <History size={14} /> Histórico
                  </Button>
                  {podeEscrever && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => abrirEdicao(f)}
                              data-testid={`btn-forn-editar-${f.id}`}>
                        <PencilLine size={14} /> Editar
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => confirmarToggle(f)}
                              data-testid={`btn-forn-toggle-${f.id}`}>
                        {f.ativo ? <><PowerOff size={14} /> Desativar</> : <><Power size={14} /> Ativar</>}
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Desktop: tabela */}
          <div className="hidden md:block bg-[#FFFFFF] border border-stone-200 rounded-lg overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-stone-50 hover:bg-stone-50">
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500">Razão social / Nome</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500">Fantasia</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500">Tipo</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500">CPF/CNPJ</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500">E-mail</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500">Contato</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500">Cidade/UF</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500">Status</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500 text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {itensFiltradosBusca.map((f) => (
                  <TableRow key={f.id} data-testid={`forn-row-${f.id}`}>
                    <TableCell className="font-medium text-stone-900 max-w-[220px] truncate">
                      {f.razao_social_nome}
                    </TableCell>
                    <TableCell className="text-stone-700 text-sm max-w-[160px] truncate">
                      {f.nome_fantasia || "—"}
                    </TableCell>
                    <TableCell><TipoTag tipo={f.tipo_pessoa} /></TableCell>
                    <TableCell className="text-stone-700 text-sm tabular-nums">
                      {f.documento_normalizado
                        ? formatarDocumento(f.tipo_pessoa, f.documento_normalizado)
                        : "—"}
                    </TableCell>
                    <TableCell className="text-stone-700 text-sm max-w-[180px] truncate">
                      {f.email || "—"}
                    </TableCell>
                    <TableCell className="text-stone-700 text-sm">
                      {f.celular || f.telefone || "—"}
                    </TableCell>
                    <TableCell className="text-stone-700 text-sm">
                      {(f.cidade || f.estado)
                        ? [f.cidade, f.estado].filter(Boolean).join(" / ")
                        : "—"}
                    </TableCell>
                    <TableCell><StatusBadge ativo={f.ativo} /></TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => abrirDetalhe(f)}
                          aria-label={`Ver detalhes de ${f.razao_social_nome}`}
                          data-testid={`btn-forn-detalhe-${f.id}`}>
                          <Eye size={14} />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => abrirAuditoria(f)}
                          aria-label={`Auditoria de ${f.razao_social_nome}`}
                          data-testid={`btn-forn-auditoria-${f.id}`}>
                          <History size={14} />
                        </Button>
                        {podeEscrever && (
                          <>
                            <Button size="sm" variant="ghost" onClick={() => abrirEdicao(f)}
                              aria-label={`Editar ${f.razao_social_nome}`}
                              data-testid={`btn-forn-editar-${f.id}`}>
                              <PencilLine size={14} />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => confirmarToggle(f)}
                              aria-label={`${f.ativo ? "Desativar" : "Ativar"} ${f.razao_social_nome}`}
                              data-testid={`btn-forn-toggle-${f.id}`}>
                              {f.ativo ? <PowerOff size={14} /> : <Power size={14} />}
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}

      {/* Detalhes */}
      <Dialog open={detalheOpen} onOpenChange={(v) => { setDetalheOpen(v); if (!v) setDetalheAlvo(null); }}>
        <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto" data-testid="dialog-forn-detalhe">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {detalheAlvo?.tipo_pessoa === "PJ"
                ? <Building2 size={18} className="text-[#083A6B]" />
                : <Users size={18} className="text-[#083A6B]" />}
              {detalheAlvo?.razao_social_nome || "Detalhes"}
            </DialogTitle>
            <DialogDescription>
              Dados estruturados do fornecedor.
            </DialogDescription>
          </DialogHeader>
          {detalheLoading ? (
            <div className="text-center text-stone-500 py-6">Carregando...</div>
          ) : detalheAlvo ? (
            <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
              <div><div className="text-[11px] uppercase text-stone-500">Tipo</div><TipoTag tipo={detalheAlvo.tipo_pessoa} /></div>
              <div><div className="text-[11px] uppercase text-stone-500">Status</div><StatusBadge ativo={detalheAlvo.ativo} /></div>
              <div className="col-span-2">
                <div className="text-[11px] uppercase text-stone-500">
                  {detalheAlvo.tipo_pessoa === "PJ" ? "Razão Social" : "Nome"}
                </div>
                {detalheAlvo.razao_social_nome || "—"}
              </div>
              <div className="col-span-2">
                <div className="text-[11px] uppercase text-stone-500">Nome fantasia</div>
                {detalheAlvo.nome_fantasia || "—"}
              </div>
              <div>
                <div className="text-[11px] uppercase text-stone-500">
                  {detalheAlvo.tipo_pessoa === "PJ" ? "CNPJ" : "CPF"}
                </div>
                {detalheAlvo.documento_normalizado
                  ? formatarDocumento(detalheAlvo.tipo_pessoa, detalheAlvo.documento_normalizado)
                  : "—"}
              </div>
              <div>
                <div className="text-[11px] uppercase text-stone-500">E-mail</div>
                {detalheAlvo.email || "—"}
              </div>
              <div>
                <div className="text-[11px] uppercase text-stone-500">Telefone</div>
                {detalheAlvo.telefone || "—"}
              </div>
              <div>
                <div className="text-[11px] uppercase text-stone-500">Celular</div>
                {detalheAlvo.celular || "—"}
              </div>
              <div className="col-span-2">
                <div className="text-[11px] uppercase text-stone-500">Endereço</div>
                {detalheAlvo.endereco || "—"}
              </div>
              <div>
                <div className="text-[11px] uppercase text-stone-500">Cidade</div>
                {detalheAlvo.cidade || "—"}
              </div>
              <div>
                <div className="text-[11px] uppercase text-stone-500">UF</div>
                {detalheAlvo.estado || "—"}
              </div>
              <div>
                <div className="text-[11px] uppercase text-stone-500">CEP</div>
                {detalheAlvo.cep || "—"}
              </div>
              <div className="col-span-2">
                <div className="text-[11px] uppercase text-stone-500">Observações</div>
                {detalheAlvo.observacoes || "—"}
              </div>
              <div className="col-span-2 text-[11px] text-stone-500 pt-2 border-t border-stone-200">
                Criado em {detalheAlvo.created_at?.slice(0, 10) || "—"} ·
                Atualizado em {detalheAlvo.updated_at?.slice(0, 10) || "—"}
                {detalheAlvo.created_by && <> · por {detalheAlvo.created_by}</>}
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Confirmação ativar/desativar */}
      <Dialog open={!!confirmAlvo} onOpenChange={(v) => { if (!v) setConfirmAlvo(null); }}>
        <DialogContent className="max-w-md" data-testid="dialog-forn-confirmar-toggle">
          <DialogHeader>
            <DialogTitle>
              {confirmAlvo?.ativo ? "Desativar este fornecedor?" : "Ativar este fornecedor?"}
            </DialogTitle>
            <DialogDescription>
              {confirmAlvo?.ativo
                ? "Ele permanecerá registrado e poderá ser reativado. Não poderá ser usado em novos lançamentos enquanto estiver inativo."
                : "Ele voltará a aparecer disponível para novos lançamentos financeiros."}
            </DialogDescription>
          </DialogHeader>
          <div className="text-sm text-stone-700">
            <b>{confirmAlvo?.razao_social_nome}</b>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmAlvo(null)} disabled={toggling}>Cancelar</Button>
            <Button data-testid="btn-forn-confirmar-toggle" onClick={executarToggle} disabled={toggling}>
              {toggling ? "Processando..." : confirmAlvo?.ativo ? "Desativar" : "Ativar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Auditoria */}
      <Dialog open={auditOpen} onOpenChange={(v) => { setAuditOpen(v); if (!v) { setAuditAlvo(null); setAuditItems([]); } }}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="dialog-forn-auditoria">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ScrollText size={18} className="text-[#083A6B]" />
              Histórico — {auditAlvo?.razao_social_nome || "Fornecedor"}
            </DialogTitle>
            <DialogDescription>
              Ações registradas para este fornecedor, em ordem cronológica reversa.
            </DialogDescription>
          </DialogHeader>
          {auditLoading ? (
            <div className="text-center text-stone-500 py-6">Carregando histórico...</div>
          ) : auditItems.length === 0 ? (
            <div className="text-center text-stone-500 py-6">Nenhum registro de auditoria.</div>
          ) : (
            <ul className="space-y-3">
              {auditItems.map((ev) => (
                <li key={ev.id} data-testid={`forn-audit-${ev.id}`}
                    className="border border-stone-200 rounded-lg p-3 bg-white">
                  <div className="flex items-center justify-between gap-3 mb-1">
                    <span className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-[#083A6B]">
                      {acaoLabel[ev.acao] || ev.acao}
                    </span>
                    <span className="text-[11px] text-stone-500">
                      {ev.quando ? new Date(ev.quando).toLocaleString("pt-BR") : "—"}
                    </span>
                  </div>
                  <div className="text-xs text-stone-600 mb-2">
                    Por: <b>{ev.por || "—"}</b>
                    {ev.extras?.campos?.length ? (
                      <> · Campos: {ev.extras.campos.join(", ")}</>
                    ) : null}
                  </div>
                  {(ev.before || ev.after) && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-[11px] font-mono bg-stone-50 rounded p-2 max-h-64 overflow-auto">
                      <div>
                        <div className="text-stone-500 mb-1">Antes:</div>
                        <pre className="whitespace-pre-wrap break-words">
{ev.before ? JSON.stringify(ev.before, null, 2) : "—"}
                        </pre>
                      </div>
                      <div>
                        <div className="text-stone-500 mb-1">Depois:</div>
                        <pre className="whitespace-pre-wrap break-words">
{ev.after ? JSON.stringify(ev.after, null, 2) : "—"}
                        </pre>
                      </div>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
